import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';

import Button from '../common/Button';
import { apiUrl } from '../../config/api';
import logo from '../../images/logo-wordmark.png';
import './Footer.scss';

// Ordered the way a shopper meets them: delivery, paying, then the
// after-the-sale policies, with the legal text and FAQ last.
const CUSTOMER_SERVICE = [
  { to: '/policies?id=2', label: 'Shipping policy' },
  { to: '/policies?id=1', label: 'Payment policy' },
  { to: '/policies?id=3', label: 'Return and exchange policy' },
  { to: '/policies?id=4', label: 'Cancellation policy' },
  { to: '/tac', label: 'Terms and conditions' },
  { to: '/policies?id=5', label: 'Frequently asked questions' },
];

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/allproducts', label: 'Shop all' },
  { to: '/all-categories', label: 'Categories' },
  { to: '/aboutus', label: 'About us' },
  { to: '/all-best-reviews', label: 'Reviews' },
];

const CONTACT = [
  { href: 'mailto:indianjewelryish@gmail.com', label: 'indianjewelryish@gmail.com' },
  { href: 'tel:+919799279134', label: '+91 97992 79134' },
  {
    href: 'https://wa.me/+919799279134',
    label: 'Chat on WhatsApp',
    external: true,
  },
];

// Only accounts that actually exist are linked. The two placeholders that
// used `href="javascript:void(0)"` are omitted rather than rendered dead.
const SOCIALS = [
  {
    href: 'https://www.facebook.com/profile.php?id=61563907521069',
    icon: faFacebook,
    label: 'Facebook',
  },
  {
    href: 'https://www.instagram.com/indian_jewelryish?igsh=enRyZng1ZWtvZ2Ns',
    icon: faInstagram,
    label: 'Instagram',
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // { type, message }
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();

    if (!EMAIL_PATTERN.test(trimmed)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setSubmitting(true);
    setStatus(null);
    try {
      const response = await fetch(apiUrl('/api/user-subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setStatus({ type: 'success', message: 'Thank you for subscribing!' });
        setEmail('');
      } else {
        setStatus({
          type: 'error',
          message: data.message || 'Subscription failed. Please try again.',
        });
      }
    } catch {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="ij-footer">
      <div className="ij-container ij-footer__grid">
        <div className="ij-footer__brand">
          <img
            src={logo}
            alt="Indian Jewelryish"
            width="346"
            height="210"
            loading="lazy"
          />
          <p className="ij-footer__tagline">
            Handcrafted Indian jewellery — bridal sets, kundan, polki and
            everyday pieces, shipped across the USA.
          </p>
          <ul className="ij-footer__social">
            {SOCIALS.map(({ href, icon, label }) => (
              <li key={label}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <nav className="ij-footer__col" aria-labelledby="footer-service">
          <h2 className="ij-footer__heading" id="footer-service">
            Customer service
          </h2>
          <ul>
            {CUSTOMER_SERVICE.map(({ to, label }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="ij-footer__col" aria-labelledby="footer-links">
          <h2 className="ij-footer__heading" id="footer-links">
            Explore
          </h2>
          <ul>
            {QUICK_LINKS.map(({ to, label }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="ij-footer__col" >
          <h2 className="ij-footer__heading" id="footer-contact">
            Get in touch
          </h2>
          <ul aria-labelledby="footer-contact">
            {CONTACT.map(({ href, label, external }) => (
              <li key={href}>
                <a
                  href={href}
                  {...(external
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>

          <form className="ij-footer__subscribe" onSubmit={handleSubscribe} noValidate>
            <label className="ij-footer__heading" htmlFor="footer-email">
              Exclusive offers
            </label>
            <div className="ij-footer__subscribe-row">
              <input
                id="footer-email"
                type="email"
                className="ij-input"
                placeholder="Your email address"
                value={email}
                autoComplete="email"
                aria-invalid={status?.type === 'error' || undefined}
                aria-describedby={status ? 'footer-subscribe-status' : undefined}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
              />
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                loadingText="Sending"
              >
                Subscribe
              </Button>
            </div>
            {status && (
              <p
                id="footer-subscribe-status"
                role="status"
                className={
                  status.type === 'error'
                    ? 'ij-footer__status ij-footer__status--error'
                    : 'ij-footer__status ij-footer__status--success'
                }
              >
                {status.message}
              </p>
            )}
          </form>
        </div>
      </div>

      <div className="ij-footer__bar">
        <div className="ij-container">
          <p>
            &copy; {new Date().getFullYear()} Indian Jewelryish. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
